/**
 * Draggable three-pane column widths.
 *
 * The sidebar range and its persistence mirror `RootView.swift` + `UIState`
 * on the Mac (`persistSidebarWidth`); the note-list column is web-only
 * (see `columns` in tokens.ts). Clamps are pure functions so the layout
 * maths stays unit-testable, and `LibraryScreen` re-runs them on window
 * resize exactly like the Mac's `onChange(of: geo.size.width)`.
 */
import { columns, metrics } from '../../design/tokens.js';

const SIDEBAR_KEY = 'jnote.sidebarWidth';
const NOTE_LIST_KEY = 'jnote.noteListWidth';

/** Exported for the config-backup importer (same keys, single source). */
export const SIDEBAR_WIDTH_KEY = SIDEBAR_KEY;
export const NOTE_LIST_WIDTH_KEY = NOTE_LIST_KEY;

/**
 * Sidebar upper bound for a given library width: never above
 * `sidebarWidthMax`, and never so wide the content column drops below
 * `contentMinWidth` (identical to `maxSidebar` in RootView.swift).
 */
export function sidebarWidthMaxFor(viewportWidth: number): number {
  return Math.min(
    metrics.sidebarWidthMax,
    Math.max(metrics.sidebarWidthMin, viewportWidth - metrics.contentMinWidth),
  );
}

/** Note-list upper bound: the editor must keep `columns.editorMinWidth`. */
export function noteListWidthMaxFor(viewportWidth: number, sidebarWidth: number): number {
  return Math.min(
    columns.noteListWidthMax,
    Math.max(columns.noteListWidthMin, viewportWidth - sidebarWidth - columns.editorMinWidth),
  );
}

export function clampSidebarWidth(width: number, viewportWidth: number): number {
  return Math.min(Math.max(width, metrics.sidebarWidthMin), sidebarWidthMaxFor(viewportWidth));
}

export function clampNoteListWidth(
  width: number,
  viewportWidth: number,
  sidebarWidth: number,
): number {
  return Math.min(Math.max(width, columns.noteListWidthMin), noteListWidthMaxFor(viewportWidth, sidebarWidth));
}

function readStored(key: string, fallback: number, clamp: (value: number) => number): number {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return fallback;
    const parsed = Number.parseFloat(raw);
    return Number.isFinite(parsed) ? clamp(parsed) : fallback;
  } catch {
    return fallback;
  }
}

function writeStored(key: string, width: number): void {
  try {
    localStorage.setItem(key, String(Math.round(width)));
  } catch {
    // Private-mode storage failures must never break dragging.
  }
}

/** Restored sidebar width, clamped to the current window (like the Mac). */
export function loadSidebarWidth(viewportWidth: number): number {
  return readStored(SIDEBAR_KEY, metrics.sidebarWidth, (value) =>
    clampSidebarWidth(value, viewportWidth),
  );
}

export function saveSidebarWidth(width: number): void {
  writeStored(SIDEBAR_KEY, width);
}

export function loadNoteListWidth(viewportWidth: number, sidebarWidth: number): number {
  return readStored(NOTE_LIST_KEY, columns.noteListWidth, (value) =>
    clampNoteListWidth(value, viewportWidth, sidebarWidth),
  );
}

export function saveNoteListWidth(width: number): void {
  writeStored(NOTE_LIST_KEY, width);
}
